import React, { useState, useEffect, useRef, useMemo } from "react";
import { useRecoilValue, useRecoilState } from "recoil";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { CircleNotch } from "phosphor-react";
import {
  courseTreeAtom,
  courseTreeBaselineAtom,
  lessonsBySubcourseAtom,
  skillsByLessonAtom,
  lastOpenedNoteState,
} from "../../../../recoil/atoms";
import {
  getLessonsBySubcourse,
  getSkillsByLesson,
  getNotesBySkill,
} from "../../../../services/learningApi";
import { saveLastOpened } from "../../../../utils/lastOpened";
import { findSubcourseByLevel } from "./utils";
import LevelPanelMobileView from "./LevelPanelMobileView";
import LevelPanelDesktopView from "./LevelPanelDesktopView";

const LevelPanel = ({
  level,
  courseTree: courseTreeProp,
  loading: courseTreeLoading,
  onQuickPracticeStart,
  onWritingPracticeStart,
  onSpeakingPracticeStart,
  onDrillVocabStart,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper to update URL params - works with HashRouter
  // CRITICAL: setSearchParams should work with HashRouter in React Router v6
  // But we also manually update window.location.hash as fallback
  const updateUrlParams = (params) => {
    const newParams = new URLSearchParams(searchParams);

    // Update params
    Object.keys(params).forEach((key) => {
      if (
        params[key] === null ||
        params[key] === undefined ||
        params[key] === ""
      ) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(params[key]));
      }
    });

    const newSearch = newParams.toString();
    const newHash = newSearch
      ? `#${location.pathname}?${newSearch}`
      : `#${location.pathname}`;

    // CRITICAL: Update hash FIRST - this immediately changes the URL
    // Do this BEFORE setSearchParams to ensure URL updates right away
    // Use history.replaceState to prevent scroll behavior when hash changes
    if (window.location.hash !== newHash) {
      // Save current scroll position
      const scrollY = window.scrollY;
      
      // Update hash using history API to prevent scroll
      window.history.replaceState(
        { ...window.history.state, scrollY },
        '',
        newHash
      );
      
      // Restore scroll position immediately to prevent scroll to top
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });
    }

    // Then sync with React Router state
    setSearchParams(newParams, { replace: true });
  };

  // Use prop if provided, otherwise use Recoil, fallback to baseline
  // CRITICAL: Jika sedang loading, jangan pakai baseline untuk prevent show old level
  const courseTreeFromRecoil = useRecoilValue(courseTreeAtom);
  const courseTreeBaseline = useRecoilValue(courseTreeBaselineAtom);
  const courseTree = courseTreeLoading
    ? courseTreeProp || courseTreeFromRecoil // Jangan pakai baseline saat loading
    : courseTreeProp || courseTreeFromRecoil || courseTreeBaseline;
  const [lessonsCache, setLessonsCache] = useRecoilState(
    lessonsBySubcourseAtom
  );
  const [skillsCache, setSkillsCache] = useRecoilState(skillsByLessonAtom);
  const lastOpenedNote = useRecoilValue(lastOpenedNoteState);

  // Fallback: load dari localStorage jika Recoil belum ter-load
  const getLastOpenedNote = () => {
    if (lastOpenedNote) {
      return lastOpenedNote;
    }
    // Fallback ke localStorage
    try {
      const saved = localStorage.getItem("lastOpenedNote");
      const parsed = saved ? JSON.parse(saved) : null;
      return parsed;
    } catch (e) {
      return null;
    }
  };

  const [lessonsLoading, setLessonsLoading] = useState(false);
  const lessonsCacheRef = useRef(lessonsCache);

  // Update ref when cache changes
  useEffect(() => {
    lessonsCacheRef.current = lessonsCache;
  }, [lessonsCache]);

  // Get subcourse from course tree to extract progress
  const subcourseFromTree = useMemo(() => {
    return courseTree ? findSubcourseByLevel(courseTree, level) : null;
  }, [courseTree, level]);

  const userStats = subcourseFromTree?.["user-stats"] || {};
  const allStats = userStats["all-stats"] || {};
  const quickStats = userStats["quick-stats"] || {};
  const writingStats = userStats["writing-stats"] || {};
  const speakingStats = userStats["speaking-stats"] || {};
  // changed by Cursor: extract vocabStats and vocabReady from course-tree (vocab-ready? field)
  const vocabStats = userStats["vocab-stats"] || {};
  const vocabReady = subcourseFromTree?.["vocab-ready?"] === true;

  // Use progress from API - hanya show jika tidak loading atau courseTree sudah ter-fetch
  // Jika loading, jangan show old data, biarkan loading indicator yang handle
  const progress = courseTreeLoading ? null : allStats.progress ?? 0;
  const levelNumber = courseTreeLoading ? null : allStats.level ?? 0;
  // changed by Cursor: calculate isMastered based on level >= 80
  // Check if levelNumber is a valid number and >= 80, and not loading
  const isMastered = !courseTreeLoading && typeof levelNumber === 'number' && levelNumber >= 80;
  
  // Debug log untuk isMastered
  console.log('🎉 isMastered check:', {
    courseTreeLoading,
    levelNumber,
    allStatsLevel: allStats.level,
    isMastered,
    typeOfLevelNumber: typeof levelNumber,
    subcourseFromTree: subcourseFromTree?._id || subcourseFromTree?.id
  });

  // Use practice stats from API - hanya show jika tidak loading
  const practices = {
    quick: {
      level: courseTreeLoading ? null : quickStats.level ?? 0,
      progress: courseTreeLoading ? null : quickStats.progress ?? 0,
    },
    writing: {
      level: courseTreeLoading ? null : writingStats.level ?? 0,
      progress: courseTreeLoading ? null : writingStats.progress ?? 0,
    },
    speaking: {
      level: courseTreeLoading ? null : speakingStats.level ?? 0,
      progress: courseTreeLoading ? null : speakingStats.progress ?? 0,
    },
  };

  // Track last fetched subcourse-id to prevent duplicate fetches
  const lastFetchedSubcourseIdRef = useRef(null);
  const lastLevelRef = useRef(null); // Track last level to detect tab changes

  // Extract subcourse-id from URL params for stable comparison
  const urlSubcourseId = searchParams.get("subcourse-id");

  // Extract target subcourse-id from level (useMemo for stable dependency)
  // CRITICAL: Always use subcourse-id from level to ensure correct lessons for each tab
  const targetSubcourseId = useMemo(() => {
    console.log('🔍 LevelPanel: Finding subcourse for level:', level, 'courseTree:', !!courseTree);
    
    if (!courseTree) {
      console.warn('⚠️ LevelPanel: courseTree not available yet');
      return null;
    }
    
    if (!level) {
      console.warn('⚠️ LevelPanel: level is null/undefined');
      return null;
    }

    // CRITICAL: Always get subcourse-id from level (A1, A2, B1, B2, etc.)
    // This ensures each tab shows correct lessons
    const subcourse = findSubcourseByLevel(courseTree, level);
    console.log('🔍 LevelPanel: findSubcourseByLevel result:', subcourse ? { id: subcourse.id || subcourse._id, name: subcourse.name } : null);
    
    if (subcourse) {
      const subcourseId = subcourse?.id || subcourse?._id;
      if (subcourseId) {
        console.log('✅ LevelPanel: Target subcourse-id from level:', { level, subcourseId, subcourseName: subcourse.name });
        return subcourseId;
      } else {
        console.warn('⚠️ LevelPanel: Subcourse found but no id:', subcourse);
      }
    }

    console.warn('⚠️ LevelPanel: No subcourse found for level:', level, 'courseTree structure:', {
      hasCourses: !!courseTree.courses,
      coursesLength: courseTree.courses?.length,
      isArray: Array.isArray(courseTree)
    });
    return null;
  }, [courseTree, level]); // Only depend on courseTree and level

  // Fetch lessons by subcourse based on URL params or level (with Recoil cache)
  useEffect(() => {
    const fetchLessons = async () => {
      if (!courseTree || !targetSubcourseId) {
        return;
      }

      // CRITICAL: Detect level/tab change and force refetch
      const levelChanged = lastLevelRef.current !== null && lastLevelRef.current !== level;
      
      if (levelChanged) {
        // Reset ref when level changes to force refetch
        lastFetchedSubcourseIdRef.current = null;
        console.log('🔄 Level changed, forcing lesson refetch:', { 
          oldLevel: lastLevelRef.current, 
          newLevel: level,
          subcourseId: targetSubcourseId
        });
        
        // Update URL params to sync with new level
        if (targetSubcourseId) {
          updateUrlParams({
            "subcourse-id": targetSubcourseId,
            "lesson-id": null, // Clear lesson-id when level changes
            "skill-id": null, // Clear skill-id when level changes
          });
        }
      }
      lastLevelRef.current = level;

      // CRITICAL: Check if lessons already cached in Recoil - skip fetch if exists
      // But only skip if we're not switching tabs (level hasn't changed)
      const cached = lessonsCacheRef.current[targetSubcourseId];
      if (
        !levelChanged &&
        cached &&
        cached.lessons &&
        Array.isArray(cached.lessons) &&
        cached.lessons.length > 0 &&
        lastFetchedSubcourseIdRef.current === targetSubcourseId
      ) {
        // Only skip if we already fetched this exact subcourse and level hasn't changed
        return;
      }

      // CRITICAL: Skip jika subcourse-id sama dengan yang sedang di-fetch atau sudah di-fetch
      // But allow if level changed (force refetch)
      if (!levelChanged && lastFetchedSubcourseIdRef.current === targetSubcourseId) {
        return;
      }

      // Check if already loading
      if (cached && cached.loading) {
        return;
      }

      // Mark as fetching BEFORE setting loading state
      lastFetchedSubcourseIdRef.current = targetSubcourseId;

      // Set loading state in cache
      setLessonsCache((prev) => ({
        ...prev,
        [targetSubcourseId]: {
          ...prev[targetSubcourseId],
          loading: true,
          error: null,
        },
      }));
      setLessonsLoading(true);

      try {
        const result = await getLessonsBySubcourse(targetSubcourseId);

        if (result.success) {
          const lessonsData = Array.isArray(result.data) ? result.data : [];

          // Save to Recoil cache
          setLessonsCache((prev) => ({
            ...prev,
            [targetSubcourseId]: {
              lessons: lessonsData,
              loading: false,
              error: null,
            },
          }));
          // Ref already set, keep it to prevent re-fetch
        } else {
          setLessonsCache((prev) => ({
            ...prev,
            [targetSubcourseId]: {
              lessons: [],
              loading: false,
              error: result.message,
            },
          }));
          // Reset ref on error so we can retry later
          if (lastFetchedSubcourseIdRef.current === targetSubcourseId) {
            lastFetchedSubcourseIdRef.current = null;
          }
        }
      } catch (error) {
        setLessonsCache((prev) => ({
          ...prev,
          [targetSubcourseId]: {
            lessons: [],
            loading: false,
            error: error.message,
          },
        }));
        // Reset ref on error so we can retry later
        if (lastFetchedSubcourseIdRef.current === targetSubcourseId) {
          lastFetchedSubcourseIdRef.current = null;
        }
      } finally {
        setLessonsLoading(false);
      }
    };

    fetchLessons();
  }, [courseTree, targetSubcourseId, level]); // Add level as dependency to refetch when tab changes

  // Get lessons from Recoil cache
  // CRITICAL: Always use subcourse-id from level to ensure correct lessons for each tab
  // Use useMemo untuk prevent re-compute setiap render
  const cachedData = useMemo(() => {
    if (!courseTree || !level) return { lessons: [], loading: false };

    // CRITICAL: Always get subcourse-id from level (same as targetSubcourseId)
    const subcourse = findSubcourseByLevel(courseTree, level);
    if (!subcourse) return { lessons: [], loading: false };
    
    const targetSubcourseId = subcourse.id || subcourse._id;
    if (!targetSubcourseId) return { lessons: [], loading: false };

    const cached = lessonsCache[targetSubcourseId];

    if (cached) {
      return {
        lessons: cached.lessons || [],
        loading: cached.loading || false,
      };
    }

    return { lessons: [], loading: false };
  }, [courseTree, level, lessonsCache]); // Remove searchParams dependency, always use level

  // Use lessons from Recoil cache
  const displayLessons = cachedData.lessons;

  // Combine loading state from cache and local state
  const isLessonsLoading = lessonsLoading || cachedData.loading;

  const [activeLessonId, setActiveLessonId] = useState(null);
  const [selectedSkillId, setSelectedSkillId] = useState(null);

  // State for skills and notes
  const [skills, setSkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);

  // Track apakah sudah restore dari lastOpenedNote
  const hasRestoredRef = useRef(false);
  const restoredSkillIdRef = useRef(null);
  const restoredLessonIdRef = useRef(null);

  // CRITICAL: Track request yang sedang berjalan untuk prevent race condition
  const currentLessonIdRef = useRef(null);
  const cancelledRequestRef = useRef(new Set());
  // CRITICAL: Track notes request untuk prevent multiple calls
  const notesAbortControllerRef = useRef(null);
  const lastFetchedSkillIdRef = useRef(null);
  const isFetchingNotesRef = useRef(false);
  const notesDebounceTimerRef = useRef(null);
  const pendingSkillIdRef = useRef(null); // Track skillId yang sedang pending untuk di-fetch

  // CRITICAL: Track apakah initial lesson sudah di-set untuk prevent duplicate calls
  const initialLessonSetRef = useRef(false);
  const userClickedLessonRef = useRef(false);
  const userClickedSkillRef = useRef(false); // Track if user manually clicked skill
  // changed by Cursor: Track skillId yang di-klik user dari mobile tree untuk prevent override
  const treeClickedSkillIdRef = useRef(null);

  // Fetch skills when activeLessonId changes
  // CRITICAL: Skills HARUS di-fetch ulang setiap kali lesson berubah
  // CRITICAL: Cancel request sebelumnya untuk prevent race condition
  useEffect(() => {
    // CRITICAL: Mark semua request sebelumnya sebagai cancelled
    const previousLessonId = currentLessonIdRef.current;
    if (previousLessonId && previousLessonId !== activeLessonId) {
      cancelledRequestRef.current.add(previousLessonId);
    }

    // CRITICAL: Remove current lessonId dari cancelled set (jika ada) untuk allow request baru
    if (cancelledRequestRef.current.has(activeLessonId)) {
      cancelledRequestRef.current.delete(activeLessonId);
    }

    // Update current lesson ID
    currentLessonIdRef.current = activeLessonId;

    const fetchSkills = async () => {
      // CRITICAL: Capture lessonId di awal untuk check di akhir
      const lessonIdForThisRequest = activeLessonId;

      if (!lessonIdForThisRequest) {
        // Reset skills saat tidak ada lesson yang aktif
        setSkills([]);
        setSelectedSkillId(null);
        setSkillsLoading(false);
        currentLessonIdRef.current = null;
        return;
      }

      // CRITICAL: Check Recoil cache dulu sebelum fetch
      const cachedSkills = skillsCache[lessonIdForThisRequest];
      if (
        cachedSkills &&
        cachedSkills.skills &&
        cachedSkills.skills.length > 0
      ) {
        // Check lagi sebelum set state
        if (currentLessonIdRef.current === lessonIdForThisRequest) {
          setSkills(cachedSkills.skills);
          setSkillsLoading(false);
          // changed by Cursor: Check skipAutoSelectSkillRef before auto-selecting
          // This ensures mobile reading list flow works correctly with cached data
          if (skipAutoSelectSkillRef.current) {
            skipAutoSelectSkillRef.current = false; // Reset flag
            setSelectedSkillId(null); // Don't auto-select, let user pick
          } else if (!selectedSkillId && cachedSkills.skills.length > 0) {
            // Auto-select skill pertama jika belum ada yang di-select
            const firstSkillId =
              cachedSkills.skills[0].id || cachedSkills.skills[0]._id;
            setSelectedSkillId(firstSkillId);
          }
          return;
        }
      }

      // CRITICAL: Reset skills saat lesson berubah
      // Ini memastikan reading list ter-update dengan data baru dari backend
      setSkills([]); // Clear skills dulu untuk prevent stale data
      // changed by Cursor: Jangan reset selectedSkillId jika user baru klik dari tree
      if (!treeClickedSkillIdRef.current) {
        setSelectedSkillId(null); // Reset selected skill hanya jika bukan dari tree click
      }
      setSkillsLoading(true);

      try {
        // CRITICAL: Log lessonId yang akan di-fetch untuk debugging

        const result = await getSkillsByLesson(lessonIdForThisRequest);

        // CRITICAL: Check apakah request sudah di-cancel atau lessonId sudah berubah
        if (
          cancelledRequestRef.current.has(lessonIdForThisRequest) ||
          currentLessonIdRef.current !== lessonIdForThisRequest
        ) {
          return; // Jangan update state jika request sudah di-cancel atau lesson sudah berubah
        }

        if (result.success) {
          // Handle berbagai format response dari backend
          let skillsData = [];

          if (Array.isArray(result.data)) {
            // Format 1: Array langsung - ini yang paling umum
            // Backend langsung return array skills untuk lesson yang di-request
            skillsData = result.data;
          } else if (result.data && typeof result.data === "object") {
            // Format 2: Object dengan lesson-id sebagai key
            // Backend mungkin return: { "lesson-id-1": [skills...], "lesson-id-2": [skills...] }
            const lessonIdKey = lessonIdForThisRequest;

            // CRITICAL: Cari key yang match dengan lessonId yang di-request
            // Jangan ambil key pertama yang ditemukan karena itu bisa dari lesson lain!
            const matchingKey = Object.keys(result.data).find((key) => {
              // Exact match
              if (key === lessonIdKey || key === String(lessonIdKey))
                return true;
              // Check jika key mengandung lessonId atau sebaliknya
              if (
                key.includes(lessonIdKey) ||
                String(lessonIdKey).includes(key)
              )
                return true;
              return false;
            });

            if (matchingKey && Array.isArray(result.data[matchingKey])) {
              skillsData = result.data[matchingKey];
            } else {
              // CRITICAL: Jangan fallback ke key pertama! Itu bisa dari lesson lain
              // Jika tidak ada key yang match, berarti format tidak sesuai atau data tidak ada
              skillsData = []; // Set empty array, jangan ambil data dari lesson lain!
            }
          }

          // CRITICAL: Double check lagi sebelum update state
          if (currentLessonIdRef.current !== lessonIdForThisRequest) {
            return;
          }

          // CRITICAL: Validasi - pastikan skills tidak kosong dan berbeda dari sebelumnya
          if (skillsData.length === 0) {
          }

          // CRITICAL: Set skills dari backend - ini akan update reading list
          // Pastikan ini benar-benar di-call dengan skillsData yang benar
          setSkills(skillsData);

          // CRITICAL: Simpan ke Recoil cache setelah fetch
          setSkillsCache((prev) => ({
            ...prev,
            [lessonIdForThisRequest]: {
              skills: skillsData,
              loading: false,
              error: null,
            },
          }));

          // CRITICAL: Check lagi sebelum restore logic
          if (currentLessonIdRef.current !== lessonIdForThisRequest) {
            return;
          }

          // PRIORITAS 1: Restore skill dari URL params jika ada
          if (courseTree) {
            const subcourse = findSubcourseByLevel(courseTree, level);
            const subcourseId = subcourse?.id || subcourse?._id;
            if (subcourseId) {
              const urlSubcourseId = searchParams.get("subcourse-id");
              const urlLessonId = searchParams.get("lesson-id");
              const urlSkillId = searchParams.get("skill-id");

              // Jika URL params ada dan cocok dengan lesson yang sedang di-fetch
              if (
                urlSubcourseId &&
                String(urlSubcourseId) === String(subcourseId) &&
                urlLessonId &&
                String(urlLessonId) === String(lessonIdForThisRequest) &&
                urlSkillId
              ) {
                const matchingSkill = skillsData.find((skill) => {
                  const skillId = skill.id || skill._id;
                  return String(skillId) === String(urlSkillId);
                });

                if (matchingSkill) {
                  const skillId = matchingSkill.id || matchingSkill._id;
                  // Check lagi sebelum set state
                  if (currentLessonIdRef.current === lessonIdForThisRequest) {
                    setSelectedSkillId(skillId);
                    restoredSkillIdRef.current = skillId;
                    restoredLessonIdRef.current = lessonIdForThisRequest;
                    setSkillsLoading(false);
                    return; // Jangan lanjut ke fallback
                  }
                } else {
                }
              }
            }
          }

          // PRIORITAS 2: Restore skill terakhir yang dibuka jika ada dan cocok dengan lessonId dan skills yang ada
          const savedLastNote = getLastOpenedNote();
          const shouldRestoreSkill =
            savedLastNote &&
            savedLastNote.skillId &&
            savedLastNote.lessonId &&
            String(savedLastNote.lessonId) === String(lessonIdForThisRequest) &&
            (restoredLessonIdRef.current !== lessonIdForThisRequest ||
              !restoredSkillIdRef.current);

          if (shouldRestoreSkill) {
            const matchingSkill = skillsData.find((skill) => {
              const skillId = skill.id || skill._id;
              return String(skillId) === String(savedLastNote.skillId);
            });

            if (matchingSkill) {
              const skillId = matchingSkill.id || matchingSkill._id;
              // Check lagi sebelum set state
              if (currentLessonIdRef.current === lessonIdForThisRequest) {
                setSelectedSkillId(skillId);
                restoredSkillIdRef.current = skillId;
                restoredLessonIdRef.current = lessonIdForThisRequest;
                setSkillsLoading(false);
                return; // Jangan lanjut ke fallback
              }
            }
          }

          // Jika sudah pernah restore untuk lesson ini, jangan ubah selectedSkillId
          if (
            restoredLessonIdRef.current === lessonIdForThisRequest &&
            restoredSkillIdRef.current
          ) {
            // Pastikan skill yang di-restore masih ada di skillsData
            const restoredSkillStillExists = skillsData.find((skill) => {
              const skillId = skill.id || skill._id;
              return String(skillId) === String(restoredSkillIdRef.current);
            });

            if (restoredSkillStillExists) {
              // Check lagi sebelum set state
              if (currentLessonIdRef.current === lessonIdForThisRequest) {
                setSelectedSkillId(restoredSkillIdRef.current);
                setSkillsLoading(false);
                return; // Jangan ubah, sudah di-restore sebelumnya
              }
            }
          }

          // Fallback: pilih skill pertama jika tidak ada lastOpenedNote atau skill tidak ditemukan
          // TAPI hanya jika belum pernah restore untuk lesson ini
          if (
            !hasRestoredRef.current ||
            restoredLessonIdRef.current !== lessonIdForThisRequest
          ) {
            // Check lagi sebelum set state
            if (currentLessonIdRef.current === lessonIdForThisRequest) {
              // changed by Cursor: Skip auto-select if flag is set (mobile reading list flow)
              // Let user pick from reading list first instead of auto-selecting
              // changed by Cursor: Skip auto-select jika user baru klik dari tree (preserve pilihan user)
              if (treeClickedSkillIdRef.current) {
                // User klik dari tree, jangan override selectedSkillId
              } else if (skipAutoSelectSkillRef.current) {
                skipAutoSelectSkillRef.current = false; // Reset flag
                // Don't auto-select, let user pick from reading list
                setSelectedSkillId(null);
              } else if (skillsData.length > 0) {
                const firstSkillId = skillsData[0].id || skillsData[0]._id;
                setSelectedSkillId(firstSkillId);

                // CRITICAL: Update URL params immediately when auto-selecting first skill
                // This ensures URL params are always in sync with selected skill
                if (courseTree) {
                  const subcourse = findSubcourseByLevel(courseTree, level);
                  const subcourseId = subcourse?.id || subcourse?._id;
                  if (subcourseId) {
                    updateUrlParams({
                      "subcourse-id": subcourseId,
                      "lesson-id": lessonIdForThisRequest,
                      "skill-id": firstSkillId,
                    });
                  }
                }
              } else {
                // Jika tidak ada skills, tetap set null (akan tampil "No skills yet" di UI)
                setSelectedSkillId(null);
              }
            }
          }
        } else {
          // Check lagi sebelum set error state
          if (currentLessonIdRef.current === lessonIdForThisRequest) {
            setSkills([]);
            setSelectedSkillId(null);
          }
        }
      } catch (error) {
        // Jangan update state jika request sudah outdated
        if (currentLessonIdRef.current !== lessonIdForThisRequest) {
          return;
        }

        // Jangan log error kalau request di-cancel
        if (error.name !== "AbortError" && error.code !== "ECONNABORTED") {
        }

        if (currentLessonIdRef.current === lessonIdForThisRequest) {
          setSkills([]);
          setSelectedSkillId(null);
        }
      } finally {
        // Hanya update loading state jika request masih valid
        if (currentLessonIdRef.current === lessonIdForThisRequest) {
          setSkillsLoading(false);
        }
      }
    };

    fetchSkills();

    // CRITICAL: Cleanup function untuk cancel request saat lesson berubah atau component unmount
    return () => {
      // Mark request ini sebagai cancelled
      cancelledRequestRef.current.add(activeLessonId);
      // Cleanup cancelled requests yang sudah lama (lebih dari 10 detik)
      const now = Date.now();
      // Reset current lesson ID saat cleanup jika masih sama
      if (currentLessonIdRef.current === activeLessonId) {
        currentLessonIdRef.current = null;
      }
    };
    // CRITICAL: Tambahkan searchParams dan courseTree untuk restore skill dari URL params
  }, [activeLessonId, searchParams, courseTree, level]);

  // CRITICAL: Reset dan re-fetch skills ketika displayLessons berubah (subcourse berubah)
  // Ini memastikan skills ter-update ketika pindah subcourse, bahkan jika activeLessonId sama
  const previousDisplayLessonsRef = useRef([]);
  useEffect(() => {
    // CRITICAL: Skip jika sudah restore dari practice (prevent override)
    if (restoredFromPracticeRef.current) {
      // Update ref untuk next comparison tapi jangan ubah activeLessonId
      previousDisplayLessonsRef.current = displayLessons.map(
        (l) => l.id || l._id
      );
      return;
    }

    // CRITICAL: Skip jika masih ada prev-subcourse yang belum di-restore (tunggu restore dulu)
    try {
      const prevSubcourseStr = localStorage.getItem("prev-subcourse");
      if (prevSubcourseStr) {
        const prevSubcourse = JSON.parse(prevSubcourseStr);
        const age = Date.now() - (prevSubcourse.timestamp || 0);
        if (age <= 10 * 60 * 1000 && prevSubcourse["subcourse-id"]) {
          // Get current subcourse-id untuk compare
          const currentSubcourse = findSubcourseByLevel(courseTree, level);
          const currentSubcourseId =
            currentSubcourse?.id || currentSubcourse?._id || null;
          // Jika subcourse-id belum match, berarti masih loading atau belum di-restore, tunggu dulu
          if (
            String(prevSubcourse["subcourse-id"]) !== String(currentSubcourseId)
          ) {
            previousDisplayLessonsRef.current = displayLessons.map(
              (l) => l.id || l._id
            );
            return;
          }
        }
      }
    } catch (e) {
      // Ignore error
    }

    // Check jika displayLessons benar-benar berubah (subcourse berubah)
    const lessonsChanged =
      JSON.stringify(previousDisplayLessonsRef.current) !==
      JSON.stringify(displayLessons.map((l) => l.id || l._id));

    if (lessonsChanged && displayLessons.length > 0) {
      // PRIORITAS: Cek URL params dulu sebelum reset lesson
      const urlSubcourseId = searchParams.get("subcourse-id");
      const urlLessonId = searchParams.get("lesson-id");
      const currentSubcourse = findSubcourseByLevel(courseTree, level);
      const currentSubcourseId = currentSubcourse?.id || currentSubcourse?._id;
      const hasUrlParams =
        urlSubcourseId &&
        urlLessonId &&
        String(urlSubcourseId) === String(currentSubcourseId);

      // Reset skills dan force re-fetch untuk lesson yang aktif
      setSkills([]);
      setSelectedSkillId(null);
      setSkillsLoading(false);
      currentLessonIdRef.current = null;
      cancelledRequestRef.current.clear();

      // Jika activeLessonId ada dan masih ada di displayLessons baru, re-fetch skills
      if (activeLessonId) {
        const lessonExists = displayLessons.some((lesson) => {
          const lessonId = lesson.id || lesson._id;
          return String(lessonId) === String(activeLessonId);
        });

        if (lessonExists) {
          // Lesson masih ada, cek cache dulu
          const cachedSkills = skillsCache[activeLessonId];
          if (
            cachedSkills &&
            cachedSkills.skills &&
            cachedSkills.skills.length > 0
          ) {
            // Load dari cache
            setSkills(cachedSkills.skills);
            const firstSkillId =
              cachedSkills.skills[0].id || cachedSkills.skills[0]._id;
            setSelectedSkillId(firstSkillId);
          } else {
            // Reset currentLessonIdRef agar useEffect fetchSkills jalan lagi
            currentLessonIdRef.current = null;
            // CRITICAL: Jangan reset activeLessonId ke null karena bisa trigger default lesson selection
            // Cukup reset currentLessonIdRef saja, useEffect fetchSkills akan jalan otomatis
            // karena activeLessonId masih sama tapi currentLessonIdRef sudah null
          }
        } else {
          // Lesson tidak ada di subcourse baru
          // PRIORITAS: Cek URL params dulu, jika ada dan lesson ada di displayLessons, restore dari URL
          if (hasUrlParams) {
            const urlLessonExists = displayLessons.some((lesson) => {
              const lessonId = lesson.id || lesson._id;
              return String(lessonId) === String(urlLessonId);
            });

            if (urlLessonExists) {
              setActiveLessonId(urlLessonId);
              initialLessonSetRef.current = true; // Prevent default lesson logic
              return;
            }
          }

          // Fallback: buka lesson pertama hanya jika belum restore dari practice dan tidak ada URL params
          if (!restoredFromPracticeRef.current && !hasUrlParams) {
            const firstLesson = displayLessons[0];
            const firstLessonId = firstLesson.id || firstLesson._id;

            // Cek cache dulu
            const cachedSkills = skillsCache[firstLessonId];
            if (
              cachedSkills &&
              cachedSkills.skills &&
              cachedSkills.skills.length > 0
            ) {
              setActiveLessonId(firstLessonId);
              setSkills(cachedSkills.skills);
              const firstSkillId =
                cachedSkills.skills[0].id || cachedSkills.skills[0]._id;
              setSelectedSkillId(firstSkillId);
            } else {
              setActiveLessonId(firstLessonId);
            }
          } else {
          }
        }
      }
    }

    // Update ref untuk next comparison
    previousDisplayLessonsRef.current = displayLessons.map(
      (l) => l.id || l._id
    );
  }, [
    displayLessons,
    activeLessonId,
    skillsCache,
    courseTree,
    level,
    searchParams,
  ]);

  // Fetch notes when selectedSkillId changes
  useEffect(() => {
    // Clear debounce timer jika ada
    if (notesDebounceTimerRef.current) {
      clearTimeout(notesDebounceTimerRef.current);
      notesDebounceTimerRef.current = null;
    }

    if (!selectedSkillId) {
      setNotes([]);
      lastFetchedSkillIdRef.current = null;
      isFetchingNotesRef.current = false;
      pendingSkillIdRef.current = null;
      return;
    }

    // CRITICAL: Saat ganti skill, kosongkan notes dulu supaya tidak tampil notes skill lama
    if (lastFetchedSkillIdRef.current !== null && lastFetchedSkillIdRef.current !== selectedSkillId) {
      setNotes([]);
    }

    // CRITICAL: Skip hanya jika skillId sama dengan yang baru saja di-fetch (prevent duplicate calls)
    // Tapi kalau skillId berbeda, HARUS fetch (user ganti reading list)
    if (lastFetchedSkillIdRef.current === selectedSkillId) {
      // SkillId sama dengan yang sudah di-fetch
      // Skip jika tidak sedang fetching dan tidak ada pending (berarti sudah selesai di-fetch)
      if (!isFetchingNotesRef.current && pendingSkillIdRef.current !== selectedSkillId) {
        return; // Sudah di-fetch, skip
      }
      // Kalau ada pending untuk skillId yang sama, skip juga
      if (pendingSkillIdRef.current === selectedSkillId) {
        return; // Sudah ada pending request, skip
      }
    } else {
      // CRITICAL: SkillId berbeda - HARUS fetch (user ganti reading list)
      // Clear pending state yang berbeda
      if (pendingSkillIdRef.current && pendingSkillIdRef.current !== selectedSkillId) {
        pendingSkillIdRef.current = null;
      }
    }
    
    // CRITICAL: Skip jika skillId sama dengan yang sedang pending (prevent double call dari React double-invoke)
    // Tapi hanya jika skillId tidak berubah
    if (pendingSkillIdRef.current === selectedSkillId && lastFetchedSkillIdRef.current === selectedSkillId) {
      return; // Sudah ada pending request untuk skillId ini, skip
    }

    // CRITICAL: Cancel request sebelumnya jika ada (skillId berbeda atau refresh)
    if (notesAbortControllerRef.current) {
      notesAbortControllerRef.current.abort();
      notesAbortControllerRef.current = null;
      isFetchingNotesRef.current = false;
    }

    // CRITICAL: Mark skillId sebagai pending SEBELUM debounce untuk prevent double call
    pendingSkillIdRef.current = selectedSkillId;

    // Debounce untuk prevent rapid multiple calls
    notesDebounceTimerRef.current = setTimeout(() => {
      // Double check: skip jika skillId sudah berubah atau sudah di-fetch
      if (lastFetchedSkillIdRef.current === selectedSkillId || pendingSkillIdRef.current !== selectedSkillId) {
        isFetchingNotesRef.current = false;
        pendingSkillIdRef.current = null;
        return;
      }

      // CRITICAL: Set flag dan mark skillId SEBELUM fetch untuk prevent race condition
      isFetchingNotesRef.current = true;
      lastFetchedSkillIdRef.current = selectedSkillId;
      // Cancel request sebelumnya jika ada
      if (notesAbortControllerRef.current) {
        notesAbortControllerRef.current.abort();
        notesAbortControllerRef.current = null;
      }

      // Create new AbortController untuk request ini
      const abortController = new AbortController();
      notesAbortControllerRef.current = abortController;
      let isMounted = true;

      const fetchNotes = async () => {
        if (!isMounted || abortController.signal.aborted) return;

        setNotesLoading(true);
        try {
          const result = await getNotesBySkill(selectedSkillId, abortController.signal);
          if (isMounted && !abortController.signal.aborted) {
            if (result.success && result.data) {
              // Transform sections to notes format
              const sections = result.data.sections || [];
              const transformedNotes = sections.map((section, index) => ({
                id: section.id || `section-${index}`,
                _id: section.id || `section-${index}`,
                title: section.title || "Untitled",
                content: section.content || "",
                annotations: section.annotations || [],
              }));
              setNotes(transformedNotes);
            } else {
              setNotes([]);
            }
          }
        } catch (error) {
          // Jangan log error kalau request di-cancel
          if (error.name !== "AbortError" && error.code !== "ECONNABORTED") {
          }
          if (isMounted && !abortController.signal.aborted) {
            setNotes([]);
          }
        } finally {
          if (isMounted && !abortController.signal.aborted) {
            setNotesLoading(false);
          }
          // Clear flags
          isFetchingNotesRef.current = false;
          pendingSkillIdRef.current = null;
          // Clear abort controller jika request ini sudah selesai
          if (notesAbortControllerRef.current === abortController) {
            notesAbortControllerRef.current = null;
          }
        }
      };

      fetchNotes();
    }, 50); // Debounce 50ms untuk prevent rapid calls

    // Cleanup function untuk cancel request jika component unmount atau dependency berubah
    return () => {
      if (notesDebounceTimerRef.current) {
        clearTimeout(notesDebounceTimerRef.current);
        notesDebounceTimerRef.current = null;
      }
      if (notesAbortControllerRef.current) {
        notesAbortControllerRef.current.abort();
        notesAbortControllerRef.current = null;
      }
      isFetchingNotesRef.current = false;
      // Jangan clear pendingSkillIdRef di cleanup karena mungkin masih valid untuk call berikutnya
    };
  }, [selectedSkillId]);

  // PRIORITAS 1: Restore dari prev-subcourse (jika kembali dari practice)
  // Harus jalan SEBELUM logic default untuk prevent override
  // CRITICAL: Tunggu sampai fully loaded sebelum restore
  const restoredFromPracticeRef = useRef(false);
  const hasAttemptedRestoreRef = useRef(false);
  useEffect(() => {
    // CRITICAL: Tunggu sampai fully loaded - jangan jalan saat masih loading
    if (courseTreeLoading || isLessonsLoading || !courseTree) {
      return;
    }

    // Skip jika sudah pernah restore
    if (restoredFromPracticeRef.current) {
      return;
    }

    // Skip jika sudah pernah attempt restore (prevent multiple attempts)
    if (hasAttemptedRestoreRef.current) {
      return;
    }

    // Skip jika user sudah klik lesson secara manual
    if (userClickedLessonRef.current) {
      return;
    }

    // CRITICAL: Pastikan displayLessons sudah ready
    if (displayLessons.length === 0) {
      return;
    }

    // Jangan mark as attempted di sini, biarkan di-mark setelah berhasil restore atau setelah semua kondisi terpenuhi

    try {
      const prevSubcourseStr = localStorage.getItem("prev-subcourse");
      if (!prevSubcourseStr) {
        return;
      }

      const prevSubcourse = JSON.parse(prevSubcourseStr);

      const age = Date.now() - (prevSubcourse.timestamp || 0);

      // Only restore if timestamp is recent (within last 10 minutes)
      if (age > 10 * 60 * 1000) {
        localStorage.removeItem("prev-subcourse");
        return;
      }

      // Get current subcourse-id untuk compare
      const currentSubcourse = findSubcourseByLevel(courseTree, level);
      const currentSubcourseId =
        currentSubcourse?.id || currentSubcourse?._id || null;

      // CRITICAL: Restore hanya jika subcourse-id match (tidak perlu cek level)
      // Jika tidak match, berarti user di subcourse yang berbeda (mungkin masih loading atau belum di-restore di index.jsx)
      // Jadi skip restore dan jangan mark as attempted, biarkan coba lagi saat subcourse sudah benar
      const subcourseMatch =
        String(prevSubcourse["subcourse-id"]) === String(currentSubcourseId);

      if (!subcourseMatch) {
        // Jangan mark as attempted, biarkan coba lagi saat subcourse sudah benar
        return;
      }

      // Subcourse match, restore lesson dan skill
      if (prevSubcourse["lesson-id"] && displayLessons.length > 0) {
        const matchingLesson = displayLessons.find((lesson) => {
          const lessonId = lesson.id || lesson._id;
          return String(lessonId) === String(prevSubcourse["lesson-id"]);
        });

        if (matchingLesson) {
          const lessonId = matchingLesson.id || matchingLesson._id;
          // CRITICAL: Set refs SEBELUM setActiveLessonId untuk prevent race condition
          restoredFromPracticeRef.current = true;
          initialLessonSetRef.current = true;
          hasAttemptedRestoreRef.current = true; // Mark as attempted setelah berhasil restore
          setActiveLessonId(lessonId);
          // Set collapse lesson list ketika kembali dari practice
          setShouldCollapseLessonList(true);
          // changed by Cursor: expand lesson di mobile tree dan scroll ke lesson tsb
          setMobileExpandedLessonIds(prev => {
            const next = new Set(prev);
            next.add(lessonId);
            return next;
          });
          fetchSkillsForLessonTree(lessonId);
          // changed by Cursor: scroll ke lesson header (judul), bukan ke seluruh lesson block
          setTimeout(() => {
            const lessonEl = document.querySelector(`.mobile-lessons-tree-lesson[data-lesson-id="${lessonId}"]`);
            if (lessonEl) {
              const lessonHead = lessonEl.querySelector('.mobile-lessons-tree-lesson-head');
              (lessonHead || lessonEl).scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 500);
          return; // CRITICAL: Return early untuk prevent logic default jalan
        } else {
          // Mark as attempted karena sudah cek semua kondisi tapi lesson tidak ditemukan
          hasAttemptedRestoreRef.current = true;
        }
      } else {
        // Mark as attempted karena kondisi tidak terpenuhi
        hasAttemptedRestoreRef.current = true;
      }
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
      }
    }
  }, [
    displayLessons,
    level,
    setActiveLessonId,
    courseTree,
    courseTreeLoading,
    isLessonsLoading,
  ]); // Tambahkan loading states untuk prevent premature execution

  // PRIORITAS 2: Restore lesson terakhir yang dibuka saat lessons sudah di-fetch
  // CRITICAL: Hanya jalan sekali saat initial load, jangan trigger lagi saat user klik lesson
  // CRITICAL: Tunggu sampai fully loaded (tidak loading, lessons ready, courseTree ready)
  useEffect(() => {
    // CRITICAL: Tunggu sampai fully loaded - jangan jalan saat masih loading
    if (courseTreeLoading || isLessonsLoading || !courseTree) {
      return;
    }

    // CRITICAL: Tunggu sampai displayLessons ready
    if (displayLessons.length === 0) {
      return;
    }

    // CRITICAL: Jangan jalan jika user sudah klik lesson secara manual
    if (userClickedLessonRef.current) {
      return;
    }

    // CRITICAL: Jangan jalan jika initial lesson sudah di-set (termasuk dari prev-subcourse)
    if (initialLessonSetRef.current) {
      return;
    }

    // PRIORITAS 1: Cek URL params dulu - jika ada lesson-id di URL, skip semua logic default
    // CRITICAL: Double check untuk memastikan tidak override URL params
    if (courseTree) {
      const subcourse = findSubcourseByLevel(courseTree, level);
      const subcourseId = subcourse?.id || subcourse?._id;
      if (subcourseId) {
        const urlSubcourseId = searchParams.get("subcourse-id");
        const urlLessonId = searchParams.get("lesson-id");

        // Jika URL params ada dan subcourse-id match, berarti URL params sudah di-handle di useEffect sebelumnya
        if (
          urlSubcourseId &&
          String(urlSubcourseId) === String(subcourseId) &&
          urlLessonId
        ) {
          // Mark as set untuk prevent default lesson logic
          initialLessonSetRef.current = true;
          hasRestoredRef.current = true;
          return;
        }
      }
    }

    // CRITICAL: Double check - jika sudah ada activeLessonId dan ada URL params, jangan override
    if (activeLessonId) {
      const urlSubcourseId = searchParams.get("subcourse-id");
      const urlLessonId = searchParams.get("lesson-id");
      if (urlSubcourseId && urlLessonId) {
        const subcourse = findSubcourseByLevel(courseTree, level);
        const subcourseId = subcourse?.id || subcourse?._id;
        if (subcourseId && String(urlSubcourseId) === String(subcourseId)) {
          // URL params ada, berarti sudah di-restore di useEffect sebelumnya
          initialLessonSetRef.current = true;
          hasRestoredRef.current = true;
          return;
        }
      }
    }

    // Skip jika sudah restore dari practice
    if (restoredFromPracticeRef.current) {
      return;
    }

    const savedLastNote = getLastOpenedNote();

    // PRIORITAS 2: Cek apakah ada lastOpenedNote dengan lessonId yang cocok
    if (savedLastNote && savedLastNote.lessonId) {
      const matchingLesson = displayLessons.find((lesson) => {
        const lessonId = lesson.id || lesson._id;
        return String(lessonId) === String(savedLastNote.lessonId);
      });

      if (matchingLesson) {
        const lessonId = matchingLesson.id || matchingLesson._id;
        // CRITICAL: Hanya set jika belum ada activeLessonId atau berbeda
        if (!activeLessonId || String(activeLessonId) !== String(lessonId)) {
          initialLessonSetRef.current = true;
          setActiveLessonId(lessonId);
          hasRestoredRef.current = true;
          return;
        } else {
          initialLessonSetRef.current = true;
          hasRestoredRef.current = true;
          return;
        }
      } else {
      }
    }

    // CRITICAL: Skip jika sudah restore dari practice
    if (restoredFromPracticeRef.current) {
      return;
    }

    // CRITICAL: Final check - jika ada URL params, jangan set default lesson
    // PRIORITAS: Check URL params SEBELUM check activeLessonId
    const finalUrlSubcourseId = searchParams.get("subcourse-id");
    const finalUrlLessonId = searchParams.get("lesson-id");
    if (finalUrlSubcourseId && finalUrlLessonId) {
      const finalSubcourse = findSubcourseByLevel(courseTree, level);
      const finalSubcourseId = finalSubcourse?.id || finalSubcourse?._id;
      if (
        finalSubcourseId &&
        String(finalUrlSubcourseId) === String(finalSubcourseId)
      ) {
        // URL params ada, check apakah lesson sudah di-restore
        const lessonExists = displayLessons.some((lesson) => {
          const lessonId = lesson.id || lesson._id;
          return String(lessonId) === String(finalUrlLessonId);
        });

        if (lessonExists) {
          // URL params valid, tunggu restore dari URL params useEffect atau restore langsung di sini
          if (String(finalUrlLessonId) !== String(activeLessonId)) {
            setActiveLessonId(finalUrlLessonId);
            initialLessonSetRef.current = true;
            hasRestoredRef.current = true;
            return;
          } else {
            initialLessonSetRef.current = true;
            hasRestoredRef.current = true;
            return;
          }
        } else {
          initialLessonSetRef.current = true; // Mark as set to prevent default
          return;
        }
      }
    }

    // Fallback: pilih active lesson atau lesson pertama (hanya jika belum pernah set activeLessonId)
    // CRITICAL: Hanya set default jika benar-benar tidak ada activeLessonId dan tidak ada URL params
    if (!activeLessonId && !hasRestoredRef.current) {
      const activeLesson = displayLessons.find((lesson) => lesson.isActive);
      const defaultLessonId = activeLesson
        ? activeLesson.id || activeLesson._id
        : displayLessons[0].id || displayLessons[0]._id;
      initialLessonSetRef.current = true;
      setActiveLessonId(defaultLessonId);

      // CRITICAL: Langsung load skills dari Recoil cache jika ada, atau auto-select skill pertama setelah fetch
      const cachedSkills = skillsCache[defaultLessonId];
      if (
        cachedSkills &&
        cachedSkills.skills &&
        cachedSkills.skills.length > 0
      ) {
        setSkills(cachedSkills.skills);
        const firstSkillId =
          cachedSkills.skills[0].id || cachedSkills.skills[0]._id;
        setSelectedSkillId(firstSkillId);
      }
    } else {
      // Jika sudah ada activeLessonId atau sudah di-restore, mark sebagai sudah di-set
      initialLessonSetRef.current = true;
    }
  }, [
    displayLessons,
    activeLessonId,
    skillsCache,
    courseTree,
    level,
    searchParams,
    courseTreeLoading,
    isLessonsLoading,
  ]); // Tambahkan loading states untuk prevent premature execution

  // Reset restore flag saat level berubah
  useEffect(() => {
    hasRestoredRef.current = false;
    restoredSkillIdRef.current = null;
    restoredLessonIdRef.current = null;
    initialLessonSetRef.current = false; // CRITICAL: Reset agar bisa set initial lesson untuk level baru
    userClickedLessonRef.current = false; // CRITICAL: Reset agar bisa set initial lesson untuk level baru
    hasAttemptedRestoreRef.current = false; // CRITICAL: Reset restore attempt flag untuk level baru
    restoredFromPracticeRef.current = false; // CRITICAL: Reset restored flag untuk level baru

    // CRITICAL: Reset skills dan selectedSkillId ketika level berubah
    // Ini memastikan reading list ter-update dengan data dari subcourse baru
    setSkills([]);
    setSelectedSkillId(null);
    setSkillsLoading(false);
    currentLessonIdRef.current = null;
    cancelledRequestRef.current.clear(); // Clear semua cancelled requests
  }, [level]);

  // Save prev-subcourse sebelum practice
  // changed by Cursor: accept optional overrideLessonId untuk save lesson yang benar dari tree practice
  const savePrevSubcourse = (overrideLessonId) => {
    try {
      // Get subcourse from courseTree untuk ambil subcourse-code (identifier untuk match ke selectedLevel)
      const subcourse = courseTree
        ? findSubcourseByLevel(courseTree, level)
        : null;
      const subcourseId = subcourse?.id || subcourse?._id || null;
      // Get subcourse-code untuk restore selectedLevel (label UI)
      const subcourseCode =
        subcourse?.["subcourse-code"] ||
        subcourse?.subcourseCode ||
        subcourse?.code ||
        level;

      // changed by Cursor: only save lesson-id if explicitly provided (lesson-level practice)
      // If null/undefined, practice started from subcourse level — don't scroll to lesson on return
      const lessonIdToSave = overrideLessonId || null;
      const prevSubcourse = {
        "subcourse-code": subcourseCode, // Identifier untuk match ke selectedLevel (label UI)
        "subcourse-id": subcourseId ? String(subcourseId) : null,
        "lesson-id": lessonIdToSave ? String(lessonIdToSave) : null,
        "skill-id": selectedSkillId ? String(selectedSkillId) : null,
        timestamp: Date.now(),
      };
      localStorage.setItem("prev-subcourse", JSON.stringify(prevSubcourse));
    } catch (e) {}
  };

  // State untuk collapse lesson list ketika kembali dari practice
  const [shouldCollapseLessonList, setShouldCollapseLessonList] =
    useState(false);

  // Reset restored flag ketika level berubah
  useEffect(() => {
    restoredFromPracticeRef.current = false;
    hasAttemptedRestoreRef.current = false; // Reset restore attempt flag
    setShouldCollapseLessonList(false);
  }, [level]);

  // Restore skill dari prev-subcourse setelah skills loaded
  const restoredSkillFromPracticeRef = useRef(false);
  useEffect(() => {
    if (
      !skills.length ||
      !activeLessonId ||
      restoredSkillFromPracticeRef.current
    )
      return;

    try {
      const prevSubcourseStr = localStorage.getItem("prev-subcourse");
      if (!prevSubcourseStr) return;

      const prevSubcourse = JSON.parse(prevSubcourseStr);
      const age = Date.now() - (prevSubcourse.timestamp || 0);

      if (age > 10 * 60 * 1000) {
        localStorage.removeItem("prev-subcourse");
        return;
      }

      // Get current subcourse-id untuk compare
      const currentSubcourse = courseTree
        ? findSubcourseByLevel(courseTree, level)
        : null;
      const currentSubcourseId =
        currentSubcourse?.id || currentSubcourse?._id || null;

      // Restore skill jika ada dan cocok dengan lesson yang aktif dan subcourse cocok
      if (
        prevSubcourse["skill-id"] &&
        prevSubcourse["lesson-id"] &&
        prevSubcourse["subcourse-id"] &&
        String(prevSubcourse["subcourse-id"]) === String(currentSubcourseId) &&
        String(prevSubcourse["lesson-id"]) === String(activeLessonId)
      ) {
        const matchingSkill = skills.find((skill) => {
          const skillId = skill.id || skill._id;
          return String(skillId) === String(prevSubcourse["skill-id"]);
        });

        if (matchingSkill) {
          const skillId = matchingSkill.id || matchingSkill._id;
          setSelectedSkillId(skillId);
          restoredSkillFromPracticeRef.current = true;
          // Clear prev-subcourse setelah restore
          localStorage.removeItem("prev-subcourse");
          return;
        }
      }
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
      }
    }
  }, [skills, activeLessonId, level, setSelectedSkillId, courseTree]);

  // Reset skill restore flag ketika lesson berubah
  useEffect(() => {
    restoredSkillFromPracticeRef.current = false;
  }, [activeLessonId]);

  // Reset collapse state ketika lesson berubah (kecuali dari restore practice)
  useEffect(() => {
    // Hanya reset jika bukan dari restore practice
    if (!restoredFromPracticeRef.current && activeLessonId) {
      setShouldCollapseLessonList(false);
    }
    // Reset flag setelah digunakan
    if (restoredFromPracticeRef.current) {
      restoredFromPracticeRef.current = false;
    }
  }, [activeLessonId]);

  // Practice handlers - bisa langsung start tanpa menunggu course-tree
  // Jika course-tree belum loaded, tetap bisa start (practice akan handle sendiri)
  const handleQuickPracticeStart = (params = {}) => {
    if (!onQuickPracticeStart) return;

    // Get lessonId from params (bisa string atau object)
    const lessonId = params.lessonId || (typeof params === 'string' ? params : null);

    // changed by Cursor: pass lessonId ke savePrevSubcourse supaya save lesson yang benar
    savePrevSubcourse(lessonId);

    // Get subcourseId untuk navigasi kembali
    const subcourse = courseTree
      ? findSubcourseByLevel(courseTree, level)
      : null;
    const subcourseId = subcourse?.id || subcourse?._id;

    // changed by Cursor: guard — jangan launch practice kalau tidak ada ID sama sekali
    if (!lessonId && !subcourseId) {
      console.warn('⚠️ handleQuickPracticeStart: No lessonId or subcourseId, aborting practice start');
      return;
    }

    // Jika ada lessonId, hanya kirim lessonId (prioritas lesson-id > subcourse-id)
    // Tapi juga simpan subcourseId untuk navigasi kembali
    // Jika tidak ada lessonId, baru kirim subcourseId
    const practiceParams = lessonId 
      ? { lessonId, subcourseId }
      : { subcourseId };

    onQuickPracticeStart(practiceParams);
  };

  const handleWritingPracticeStart = (params = {}) => {
    if (!onWritingPracticeStart) return;

    // Get lessonId from params (bisa string atau object)
    const lessonId = params.lessonId || (typeof params === 'string' ? params : null);

    // changed by Cursor: pass lessonId ke savePrevSubcourse supaya save lesson yang benar
    savePrevSubcourse(lessonId);

    // Get subcourseId untuk navigasi kembali
    const subcourse = courseTree
      ? findSubcourseByLevel(courseTree, level)
      : null;
    const subcourseId = subcourse?.id || subcourse?._id;

    // Jika ada lessonId, hanya kirim lessonId (prioritas lesson-id > subcourse-id)
    // Tapi juga simpan subcourseId untuk navigasi kembali
    // Jika tidak ada lessonId, baru kirim subcourseId
    const practiceParams = lessonId 
      ? { lessonId, subcourseId } // Simpan subcourseId juga untuk navigasi kembali
      : (() => {
          return subcourseId ? { subcourseId } : {};
        })();

    // Bisa start meskipun subcourseId belum ada (practice akan handle)
    onWritingPracticeStart(practiceParams);
  };

  const handleSpeakingPracticeStart = (params = {}) => {
    if (!onSpeakingPracticeStart) return;

    // Get lessonId from params (bisa string atau object)
    const lessonId = params.lessonId || (typeof params === 'string' ? params : null);

    // changed by Cursor: pass lessonId ke savePrevSubcourse supaya save lesson yang benar
    savePrevSubcourse(lessonId);

    // Get subcourseId untuk navigasi kembali
    const subcourse = courseTree
      ? findSubcourseByLevel(courseTree, level)
      : null;
    const subcourseId = subcourse?.id || subcourse?._id;

    // Jika ada lessonId, hanya kirim lessonId (prioritas lesson-id > subcourse-id)
    // Tapi juga simpan subcourseId untuk navigasi kembali
    // Jika tidak ada lessonId, baru kirim subcourseId
    const practiceParams = lessonId 
      ? { lessonId, subcourseId } // Simpan subcourseId juga untuk navigasi kembali
      : (() => {
          return subcourseId ? { subcourseId } : {};
        })();

    // Bisa start meskipun subcourseId belum ada (practice akan handle)
    onSpeakingPracticeStart(practiceParams);
  };

  const handleDrillVocabStart = (params = {}) => {
    if (!onDrillVocabStart) return;

    const lessonId = params.lessonId || (typeof params === 'string' ? params : null);
    savePrevSubcourse(lessonId);

    const subcourse = courseTree
      ? findSubcourseByLevel(courseTree, level)
      : null;
    const subcourseId = subcourse?.id || subcourse?._id;

    const practiceParams = lessonId 
      ? { lessonId, subcourseId }
      : (() => {
          return subcourseId ? { subcourseId } : {};
        })();

    onDrillVocabStart(practiceParams);
  };

  const currentSubcourseLabel = level;
  const subcoursePracticeTitle = `${currentSubcourseLabel} Practice`;

  const activeLesson =
    displayLessons.find(
      (lesson) => (lesson.id || lesson._id) === activeLessonId
    ) || null;
  // changed by Cursor: fallback to name if title not available
  const currentLessonTitle = activeLesson?.title || activeLesson?.name || "Current Lesson";
  const lessonPracticeTitle = `${currentLessonTitle} Practice`;

  const handleLessonClick = (lessonId) => {
    // CRITICAL: Ensure lessonId is a valid string
    const validLessonId = String(lessonId);
    if (!validLessonId) {
      return;
    }

    // CRITICAL: Mark bahwa user sudah klik lesson secara manual
    // This prevents useEffect from updating URL (which would overwrite)
    userClickedLessonRef.current = true;

    // CRITICAL: Reset restore flags saat user klik lesson baru
    // Ini memastikan skills di-fetch ulang dari backend untuk lesson yang baru
    if (String(validLessonId) !== String(activeLessonId)) {
      restoredLessonIdRef.current = null;
      restoredSkillIdRef.current = null;
      hasRestoredRef.current = false;
      // Clear prev-subcourse ketika user manually pindah lesson
      try {
        localStorage.removeItem("prev-subcourse");
      } catch (e) {
        // Ignore errors
      }
    }

    // CRITICAL: Update currentLessonIdRef FIRST to ensure useEffect triggers immediately
    // Mark previous lesson as cancelled
    if (
      currentLessonIdRef.current &&
      currentLessonIdRef.current !== validLessonId
    ) {
      cancelledRequestRef.current.add(currentLessonIdRef.current);
    }
    // Remove new lesson from cancelled set if it was there
    if (cancelledRequestRef.current.has(validLessonId)) {
      cancelledRequestRef.current.delete(validLessonId);
    }
    // Update ref immediately
    currentLessonIdRef.current = validLessonId;

    // CRITICAL: Clear skills and set loading immediately for responsive UI
    // This ensures reading list updates immediately when lesson is clicked
    setSkills([]);
    setSelectedSkillId(null);
    setSkillsLoading(true);

    // Update activeLessonId - this will trigger useEffect to fetch skills
    // But we've already updated currentLessonIdRef so fetch will happen immediately
    setActiveLessonId(validLessonId);
    
    // Reset collapse state ketika lesson berubah (default terbuka)
    setShouldCollapseLessonList(false);

    // CRITICAL: Update URL params IMMEDIATELY when lesson changes
    // Always update URL params, even if courseTree is not ready yet
    // Get subcourse-id from current URL params or from courseTree
    let subcourseId = null;

    // Try to get from current URL params first
    const currentSubcourseId = searchParams.get("subcourse-id");
    if (currentSubcourseId) {
      subcourseId = currentSubcourseId;
    } else if (courseTree) {
      // Fallback to courseTree if URL params don't have it
      const subcourse = findSubcourseByLevel(courseTree, level);
      subcourseId = subcourse?.id || subcourse?._id;
    }

    // CRITICAL: Always update URL params with new lesson-id
    // This ensures URL params are always in sync with user actions
    if (subcourseId) {
      updateUrlParams({
        "subcourse-id": subcourseId,
        "lesson-id": validLessonId,
        "skill-id": null, // Clear skill-id because lesson changed
      });
    } else {
      // If we can't get subcourse-id, at least update lesson-id
      updateUrlParams({
        "lesson-id": validLessonId,
        "skill-id": null, // Clear skill-id because lesson changed
      });
    }
  };

  // changed by Cursor: jangan collapse tree ketika user klik reading material
  const handleReadingMaterialClick = () => {
    // no-op: biarkan tree tetap terbuka
  };

  const handleSkillSelect = (skillId, lessonIdFromTree) => {
    // CRITICAL: Mark that user clicked skill manually
    userClickedSkillRef.current = true;

    // changed by Cursor: jangan collapse tree ketika user klik skill

    // When skill clicked from tree, set activeLessonId to the lesson that contains this skill
    const effectiveLessonId = lessonIdFromTree ?? activeLessonId;
    if (lessonIdFromTree && String(lessonIdFromTree) !== String(activeLessonId)) {
      setActiveLessonId(lessonIdFromTree);
    }

    // Update selectedSkillId (bahkan jika sama, untuk trigger re-render)
    setSelectedSkillId(skillId);

    // CRITICAL: Update URL params immediately when skill is selected by user
    // This ensures URL updates right away when user clicks
    if (courseTree && effectiveLessonId) {
      const subcourse = findSubcourseByLevel(courseTree, level);
      const subcourseId = subcourse?.id || subcourse?._id;
      if (subcourseId) {
        updateUrlParams({
          "subcourse-id": subcourseId,
          "lesson-id": effectiveLessonId,
          "skill-id": skillId,
        });

        // Reset flag after a short delay to allow useEffect to skip
        setTimeout(() => {
          userClickedSkillRef.current = false;
        }, 100);
      }
    }
  };

  // PRIORITAS 0: Read from URL params FIRST - sebelum semua logic lain
  // CRITICAL: Ini harus jalan paling pertama untuk prevent default lesson logic
  // CRITICAL: URL params adalah source of truth - ketika lesson-id berubah, semua harus reactive
  useEffect(() => {
    // Skip if user just clicked lesson - handleLessonClick already updated URL
    if (userClickedLessonRef.current) {
      // Check if URL params now match the clicked lesson
      const urlLessonId = searchParams.get("lesson-id");
      if (urlLessonId && String(urlLessonId) === String(activeLessonId)) {
        // URL params have caught up, reset flag
        userClickedLessonRef.current = false;
      }
      return; // Don't sync from URL if user just clicked
    }

    // CRITICAL: Skip if user just clicked skill from tree (e.g. 7a)
    // Prevents URL sync from overwriting with stale URL (e.g. lesson-id=1) when navigating back
    if (userClickedSkillRef.current || treeClickedSkillIdRef.current) {
      return;
    }

    // CRITICAL: Tunggu sampai fully loaded
    if (courseTreeLoading || isLessonsLoading || !courseTree) {
      return;
    }

    // CRITICAL: Tunggu sampai displayLessons ready
    if (displayLessons.length === 0) {
      return;
    }

    const subcourse = findSubcourseByLevel(courseTree, level);
    const subcourseId = subcourse?.id || subcourse?._id;
    if (!subcourseId) return;

    const urlLessonId = searchParams.get("lesson-id");
    const urlSkillId = searchParams.get("skill-id");
    const urlSubcourseId = searchParams.get("subcourse-id");

    // Only restore from URL if subcourse-id matches
    if (urlSubcourseId && String(urlSubcourseId) === String(subcourseId)) {
      // CRITICAL: When lesson-id in URL changes, immediately update activeLessonId
      // This will trigger skills fetch, which will update reading list
      if (urlLessonId) {
        // Verify lesson exists in displayLessons before setting
        const lessonExists = displayLessons.some((lesson) => {
          const lessonId = lesson.id || lesson._id;
          return String(lessonId) === String(urlLessonId);
        });

        if (lessonExists) {
          // CRITICAL: Always sync with URL params - if lesson-id changed, update immediately
          if (String(urlLessonId) !== String(activeLessonId)) {
            // CRITICAL: Clear skills and selectedSkillId immediately when lesson changes
            // This ensures reading list and notes update immediately
            setSkills([]);
            setSelectedSkillId(null);
            setSkillsLoading(true);

            // Update refs to trigger immediate fetch
            if (
              currentLessonIdRef.current &&
              currentLessonIdRef.current !== urlLessonId
            ) {
              cancelledRequestRef.current.add(currentLessonIdRef.current);
            }
            if (cancelledRequestRef.current.has(urlLessonId)) {
              cancelledRequestRef.current.delete(urlLessonId);
            }
            currentLessonIdRef.current = urlLessonId;

            // Update activeLessonId - this will trigger useEffect to fetch skills
            setActiveLessonId(urlLessonId);

            // CRITICAL: Clear skill-id from URL if lesson changed
            // Because skills from old lesson are not valid for new lesson
            // But only if URL still has skill-id (might already be cleared by handleLessonClick)
            if (urlSkillId) {
              updateUrlParams({
                "subcourse-id": subcourseId,
                "lesson-id": urlLessonId,
                "skill-id": null, // Clear skill-id because lesson changed
              });
            }

            initialLessonSetRef.current = true;
            hasRestoredRef.current = true;
          } else {
            // Already correct, just mark as set
            initialLessonSetRef.current = true;
            hasRestoredRef.current = true;
          }
        } else {
        }
      } else {
        // No lesson-id in URL, but subcourse-id matches
      }

      // Restore skill from URL (will be set after skills are loaded)
      if (urlSkillId && String(urlSkillId) !== String(selectedSkillId)) {
        // Don't set immediately - wait for skills to be loaded
        // The skill will be restored in the skills fetch useEffect
      }
    }
  }, [
    searchParams,
    courseTree,
    level,
    displayLessons,
    activeLessonId,
    selectedSkillId,
    courseTreeLoading,
    isLessonsLoading,
  ]);

  // CRITICAL: When skill-id in URL params changes, immediately update selectedSkillId
  // This ensures notes update immediately when skill-id changes in URL
  // changed by Cursor: skip saat user baru klik skill dari tree/overlay supaya tidak override
  useEffect(() => {
    // CRITICAL: Skip jika user baru klik skill secara manual (dari tree atau Next/Prev)
    // Supaya URL sync tidak override pilihan user dengan URL lama
    if (userClickedSkillRef.current || treeClickedSkillIdRef.current) {
      return;
    }

    if (!activeLessonId || !courseTree || skills.length === 0) {
      return;
    }

    const urlSkillId = searchParams.get("skill-id");
    const urlLessonId = searchParams.get("lesson-id");
    const urlSubcourseId = searchParams.get("subcourse-id");

    // Only update if URL params match current lesson
    if (urlLessonId && String(urlLessonId) === String(activeLessonId)) {
      const subcourse = findSubcourseByLevel(courseTree, level);
      const subcourseId = subcourse?.id || subcourse?._id;

      if (urlSubcourseId && String(urlSubcourseId) === String(subcourseId)) {
        if (urlSkillId) {
          // Check if skill exists in current skills
          const matchingSkill = skills.find((skill) => {
            const skillId = skill.id || skill._id;
            return String(skillId) === String(urlSkillId);
          });

          if (matchingSkill && String(urlSkillId) !== String(selectedSkillId)) {
            setSelectedSkillId(urlSkillId);
          }
        } else if (selectedSkillId) {
          // URL has no skill-id but we have selectedSkillId - clear it
          // This happens when lesson-id changes and skill-id is cleared
          setSelectedSkillId(null);
        }
      }
    }
  }, [
    searchParams,
    activeLessonId,
    skills,
    selectedSkillId,
    courseTree,
    level,
  ]);

  // Update URL and save lastOpened whenever a skill is auto-selected (not manual user click)
  // CRITICAL: This only runs for auto-select (e.g., first skill after fetch)
  // Manual user clicks are handled by handleSkillSelect to prevent double update
  useEffect(() => {
    // Skip if user just clicked skill - handleSkillSelect already updated URL
    if (userClickedSkillRef.current) {
      return;
    }

    if (!selectedSkillId || !activeLessonId || !courseTree) {
      return;
    }

    const subcourse = findSubcourseByLevel(courseTree, level);
    const subcourseId = subcourse?.id || subcourse?._id;
    const subcourseCode =
      subcourse?.["subcourse-code"] ||
      subcourse?.subcourseCode ||
      subcourse?.code ||
      level;

    if (subcourseId && subcourseCode && activeLessonId && selectedSkillId) {
      // Check current URL params to prevent infinite loop and overwrite
      const currentSubcourseId = searchParams.get("subcourse-id");
      const currentLessonId = searchParams.get("lesson-id");
      const currentSkillId = searchParams.get("skill-id");

      // Only update URL if it's different from current state
      // This handles auto-select cases (first skill after fetch, restore from URL, etc.)
      const needsUpdate =
        String(currentSubcourseId) !== String(subcourseId) ||
        String(currentLessonId) !== String(activeLessonId) ||
        String(currentSkillId) !== String(selectedSkillId);

      if (needsUpdate) {
        updateUrlParams({
          "subcourse-id": subcourseId,
          "lesson-id": activeLessonId,
          "skill-id": selectedSkillId,
        });
      }

      // Always save to lastOpened (even if URL didn't change, in case localStorage was cleared)
      saveLastOpened({
        "subcourse-id": subcourseId,
        "lesson-id": activeLessonId,
        "skill-id": selectedSkillId,
      });
    }
  }, [selectedSkillId, activeLessonId, courseTree, level, searchParams]);

  // changed by Cursor: Persist mobileView ke localStorage agar tidak reset saat refresh
  const hasRestoredMobileViewRef = useRef(false);
  const [mobileView, setMobileView] = useState(() => {
    try {
      const saved = localStorage.getItem('mobileView');
      if (saved && ['practice', 'lessons', 'readingList'].includes(saved)) {
        hasRestoredMobileViewRef.current = true;
        return saved;
      }
      // 'reading' butuh context extra (selectedLessonForReading) yang tidak ter-persist, fallback ke readingList
      if (saved === 'reading') {
        hasRestoredMobileViewRef.current = true;
        return 'readingList';
      }
    } catch (e) {}
    return 'practice';
  });
  const [selectedLessonForReading, setSelectedLessonForReading] = useState(null);
  
  // changed by Cursor: Sub-view state for reading list page (Practice vs Reading List tabs)
  // changed by Cursor: Persist readingListSubView ke localStorage agar tidak reset saat refresh
  const [readingListSubView, setReadingListSubView] = useState(() => {
    try {
      const saved = localStorage.getItem('readingListSubView');
      if (saved && ['practice', 'list'].includes(saved)) {
        return saved;
      }
    } catch (e) {}
    return 'practice';
  });

  // changed by Cursor: Save mobileView & readingListSubView ke localStorage setiap kali berubah
  useEffect(() => {
    try {
      localStorage.setItem('mobileView', mobileView);
    } catch (e) {}
  }, [mobileView]);

  useEffect(() => {
    try {
      localStorage.setItem('readingListSubView', readingListSubView);
    } catch (e) {}
  }, [readingListSubView]);

  // changed by Cursor: Clear restore flag setelah delay agar semua initial effects selesai dulu
  // Tanpa ini, auto-switch effect bisa fire >1x saat mount (karena searchParams berubah) dan override saved state
  useEffect(() => {
    if (hasRestoredMobileViewRef.current) {
      const timer = setTimeout(() => {
        hasRestoredMobileViewRef.current = false;
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);
  
  // changed by Cursor: Skip auto-select skill on mobile - let user pick from reading list first
  const skipAutoSelectSkillRef = useRef(false);
  
  // changed by Cursor: Flag to skip auto-navigation when user is navigating forward (e.g. to readingList)
  const isForwardNavigationRef = useRef(false);

  // Check if mobile viewport
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // changed by Cursor: mobile tree - lessons below practice, expand = skills, skill click = notes overlay
  const [mobileExpandedLessonIds, setMobileExpandedLessonIds] = useState(() => new Set());
  const [desktopExpandedLessonIds, setDesktopExpandedLessonIds] = useState(() => new Set());
  const [notesOverlayOpen, setNotesOverlayOpen] = useState(false);
  const [treeLoadingLessonId, setTreeLoadingLessonId] = useState(null);
  const notesInlineContentRef = useRef(null);

  // Fetch skills for a lesson and only update Recoil cache (for tree expand)
  const fetchSkillsForLessonTree = (lessonId) => {
    const cached = skillsCache[lessonId];
    if (cached?.skills?.length > 0) return;
    if (cached?.loading) return;

    setSkillsCache((prev) => ({
      ...prev,
      [lessonId]: { ...prev[lessonId], skills: [], loading: true, error: null },
    }));
    setTreeLoadingLessonId(lessonId);

    getSkillsByLesson(lessonId)
      .then((result) => {
        let skillsData = [];
        if (result.success && Array.isArray(result.data)) {
          skillsData = result.data;
        } else if (result.success && result.data && typeof result.data === 'object') {
          const key = Object.keys(result.data).find((k) => String(k) === String(lessonId) || k.includes(lessonId));
          if (key && Array.isArray(result.data[key])) skillsData = result.data[key];
        }
        setSkillsCache((prev) => ({
          ...prev,
          [lessonId]: { skills: skillsData, loading: false, error: null },
        }));
      })
      .catch(() => {
        setSkillsCache((prev) => ({
          ...prev,
          [lessonId]: { skills: [], loading: false, error: 'Failed to load' },
        }));
      })
      .finally(() => {
        setTreeLoadingLessonId((id) => (id === lessonId ? null : id));
      });
  };

  const handleTreeToggleLesson = (lessonId) => {
    setMobileExpandedLessonIds((prev) => {
      const next = new Set(prev);
      if (next.has(lessonId)) {
        next.delete(lessonId);
      } else {
        next.add(lessonId);
        fetchSkillsForLessonTree(lessonId);
      }
      return next;
    });
  };

  // changed by Cursor: single expand - buka lesson lain otomatis tutup lesson sebelumnya
  const handleDesktopTreeToggleLesson = (lessonId) => {
    setDesktopExpandedLessonIds((prev) => {
      if (prev.has(lessonId)) {
        const next = new Set(prev);
        next.delete(lessonId);
        return next;
      }
      fetchSkillsForLessonTree(lessonId);
      return new Set([lessonId]);
    });
  };

  // auto-expand active lesson in desktop tree (single expand)
  useEffect(() => {
    if (!isMobile && activeLessonId) {
      setDesktopExpandedLessonIds((prev) => {
        if (prev.has(activeLessonId) && prev.size === 1) return prev;
        return new Set([activeLessonId]);
      });
      const cached = skillsCache[activeLessonId];
      if (!cached?.skills?.length && !cached?.loading) {
        fetchSkillsForLessonTree(activeLessonId);
      }
    }
  }, [activeLessonId, isMobile, skillsCache]);

  // changed by Cursor: set userClickedSkillRef + treeClickedSkillIdRef supaya effect lain tidak override
  const handleTreeSkillClick = (skillId, lessonId) => {
    userClickedSkillRef.current = true;
    treeClickedSkillIdRef.current = skillId;
    setActiveLessonId(lessonId);
    setSelectedSkillId(skillId);
    setNotesOverlayOpen(true);

    // Update URL langsung supaya URL sync effect tidak override dengan URL lama
    if (courseTree) {
      const subcourse = findSubcourseByLevel(courseTree, level);
      const subcourseId = subcourse?.id || subcourse?._id;
      if (subcourseId) {
        updateUrlParams({
          "subcourse-id": subcourseId,
          "lesson-id": lessonId,
          "skill-id": skillId,
        });
      }
    }

    // Reset flags setelah semua effect jalan
    setTimeout(() => {
      userClickedSkillRef.current = false;
      treeClickedSkillIdRef.current = null;
    }, 300);
  };

  const handleCloseNotesOverlay = () => {
    setNotesOverlayOpen(false);
  };

  // Setiap buka notes overlay, scroll section notes ke paling atas
  useEffect(() => {
    if (!notesOverlayOpen || !notesInlineContentRef.current) return;
    const el = notesInlineContentRef.current;
    el.scrollTop = 0;
    const t = setTimeout(() => { el.scrollTop = 0; }, 100);
    const t2 = setTimeout(() => { el.scrollTop = 0; }, 400);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [notesOverlayOpen, selectedSkillId]);

  // changed by Cursor: Mobile navigation history stack system
  // This ensures back button works sequentially: notes → readingList → lessons
  const mobileNavHistoryRef = useRef(['practice']); // Track navigation history stack
  const isNavigatingRef = useRef(false); // Prevent infinite loops during popstate

  // Helper to navigate mobile view with history tracking
  const navigateMobileView = (targetView, options = {}) => {
    const { replace = false, isBack = false } = options;
    
    if (!isMobile) {
      setMobileView(targetView);
      return;
    }

    console.log('📱 navigateMobileView:', targetView, { replace, isBack, currentHistory: [...mobileNavHistoryRef.current] });

    if (isBack) {
      // Going back - pop from history and go back in browser
      if (mobileNavHistoryRef.current.length > 1) {
        mobileNavHistoryRef.current.pop();
        const previousView = mobileNavHistoryRef.current[mobileNavHistoryRef.current.length - 1];
        isNavigatingRef.current = true;
        window.history.back();
        setMobileView(previousView);
        setTimeout(() => { isNavigatingRef.current = false; }, 100);
      } else {
        // changed by Cursor: when history has only 1 entry (e.g. refresh on readingList), still go to lessons
        const fallbackView = 'lessons';
        isNavigatingRef.current = true;
        window.history.replaceState({ mobileView: fallbackView, historyIndex: 0 }, '');
        mobileNavHistoryRef.current = [fallbackView];
        setMobileView(fallbackView);
        setTimeout(() => { isNavigatingRef.current = false; }, 100);
      }
    } else if (replace) {
      // Replace current view in history (e.g., switching between practice/lessons tabs)
      mobileNavHistoryRef.current[mobileNavHistoryRef.current.length - 1] = targetView;
      isNavigatingRef.current = true;
      window.history.replaceState({ mobileView: targetView, historyIndex: mobileNavHistoryRef.current.length - 1 }, '');
      setMobileView(targetView);
      setTimeout(() => { isNavigatingRef.current = false; }, 100);
    } else {
      // Forward navigation - push to history
      mobileNavHistoryRef.current.push(targetView);
      isNavigatingRef.current = true;
      window.history.pushState({ mobileView: targetView, historyIndex: mobileNavHistoryRef.current.length - 1 }, '');
      setMobileView(targetView);
      setTimeout(() => { isNavigatingRef.current = false; }, 100);
    }
    
    console.log('📱 navigateMobileView: Updated history stack:', [...mobileNavHistoryRef.current]);
  };

  // Handle browser back button (popstate) for mobile navigation
  useEffect(() => {
    if (!isMobile) return;

    const handlePopState = (event) => {
      if (isNavigatingRef.current) {
        console.log('📱 popstate: Ignoring (navigating programmatically)');
        return;
      }

      console.log('📱 popstate: Triggered', event.state, 'current history:', [...mobileNavHistoryRef.current]);

      // Pop from our history stack
      if (mobileNavHistoryRef.current.length > 1) {
        mobileNavHistoryRef.current.pop();
        const previousView = mobileNavHistoryRef.current[mobileNavHistoryRef.current.length - 1];
        console.log('📱 popstate: Going back to', previousView);
        setMobileView(previousView);
      } else {
        // At root of mobile navigation, let browser handle it normally
        console.log('📱 popstate: At root, letting browser handle');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isMobile]);

  // Initialize history state on mount for mobile
  useEffect(() => {
    if (!isMobile) return;
    
    // Replace current state with initial mobile view state
    window.history.replaceState({ mobileView: mobileView, historyIndex: 0 }, '');
    mobileNavHistoryRef.current = [mobileView];
    console.log('📱 Mobile nav history initialized:', mobileView);
  }, [isMobile]); // Only run when mobile status changes

  // Auto-set mobileView to 'lessons' when navigating back from practice with lesson-id in URL
  // This ensures user returns to lesson table after practice completion
  // changed by Cursor: Skip if user is navigating forward (e.g. to readingList or reading)
  // changed by Cursor: Skip on initial mount jika mobileView di-restore dari localStorage
  useEffect(() => {
    if (!isMobile) return; // Only for mobile
    
    // Skip auto-switch selama window restore aktif (ref di-clear oleh timer setelah 2 detik)
    if (hasRestoredMobileViewRef.current) {
      console.log('📱 LevelPanel: Skipping auto-nav (restored from localStorage)');
      return;
    }
    
    // Skip if user is in forward navigation (clicking lesson or skill)
    if (isForwardNavigationRef.current) {
      console.log('📱 LevelPanel: Skipping auto-nav (forward navigation in progress)');
      isForwardNavigationRef.current = false; // Reset flag
      return;
    }
    
    const urlLessonId = searchParams.get('lesson-id');
    const urlSkillId = searchParams.get('skill-id');
    
    // If URL has lesson-id but no skill-id, it means user should see lesson table
    // This happens when navigating back from practice that was started from a lesson
    // changed by Cursor: Only apply when coming back from practice, not when navigating to readingList
    if (urlLessonId && !urlSkillId && mobileView !== 'lessons' && mobileView !== 'readingList' && mobileView !== 'reading') {
      console.log('📱 LevelPanel: Auto-setting mobileView to "lessons" (returning from practice)');
      navigateMobileView('lessons', { replace: true });
    }
  }, [isMobile, searchParams, mobileView]);

  // Handle lesson click untuk mobile - navigate ke reading list
  // changed by Cursor: Don't auto-select skill on mobile - let user pick from reading list first
  const handleMobileLessonClick = (lessonId) => {
    if (isMobile) {
      // Set flag to skip auto-select skill - user should pick from reading list
      skipAutoSelectSkillRef.current = true;
      // changed by Cursor: Set forward navigation flag to prevent auto-nav useEffect interference
      isForwardNavigationRef.current = true;
      // changed by Cursor: Reset sub-view to 'practice' when entering from lessons
      // User sees practice options first, then clicks Reading List tab to see skills
      setReadingListSubView('practice');
      // Trigger lesson click untuk load skills
      handleLessonClick(lessonId);
      // changed by Cursor: Navigate ke reading list with history push
      navigateMobileView('readingList');
    } else {
      handleLessonClick(lessonId);
    }
  };

  // Handle skill select untuk mobile - navigate ke reading materials
  const handleMobileSkillSelect = (skillId) => {
    if (isMobile) {
      // changed by Cursor: Set forward navigation flag to prevent auto-nav useEffect interference
      isForwardNavigationRef.current = true;
      handleSkillSelect(skillId);
      // changed by Cursor: Navigate ke reading materials with history push
      navigateMobileView('reading');
    } else {
      handleSkillSelect(skillId);
    }
  };
  
  // changed by Cursor: Handler for mobile back navigation with history
  const handleMobileBack = () => {
    navigateMobileView(null, { isBack: true });
  };

  // Show loading state if courseTree not loaded yet - same as reading list / reading materials
  if (!courseTree || courseTreeLoading) {
    console.log('⏳ LevelPanel: Showing loading state - courseTree:', !!courseTree, 'courseTreeLoading:', courseTreeLoading);
    return (
      <div className="w-full flex flex-col gap-4 m-0 p-0 box-border">
        <div className="flex flex-col items-center justify-center gap-3 p-6 text-[var(--text-secondary)]">
          <CircleNotch size={24} weight="bold" className="animate-spin" />
          <span className="text-[14px] text-[var(--text-secondary)]">Loading lessons...</span>
        </div>
      </div>
    );
  }

  // Show error state if subcourse not found for level
  if (!targetSubcourseId) {
    console.warn('⚠️ LevelPanel: No subcourse found for level:', level, 'Showing error state');
    return (
      <div className="w-full flex flex-col gap-4 m-0 p-0 box-border p-10 text-center">
        <p>Level {level} not found. Please select a different level.</p>
      </div>
    );
  }

  // Mobile view render
  if (isMobile) {
    return (
      <LevelPanelMobileView
        level={level}
        levelNumber={levelNumber}
        progress={progress}
        courseTreeLoading={courseTreeLoading}
        isMastered={isMastered}
        practices={practices}
        vocabReady={vocabReady}
        vocabStats={vocabStats}
        notes={notes}
        notesLoading={notesLoading}
        notesOverlayOpen={notesOverlayOpen}
        selectedSkillId={selectedSkillId}
        activeLessonId={activeLessonId}
        skills={skills}
        skillsCache={skillsCache}
        displayLessons={displayLessons}
        mobileExpandedLessonIds={mobileExpandedLessonIds}
        treeLoadingLessonId={treeLoadingLessonId}
        notesInlineContentRef={notesInlineContentRef}
        courseTree={courseTree}
        onQuickPracticeStart={handleQuickPracticeStart}
        onWritingPracticeStart={handleWritingPracticeStart}
        onSpeakingPracticeStart={handleSpeakingPracticeStart}
        onDrillVocabStart={handleDrillVocabStart}
        onToggleLesson={handleTreeToggleLesson}
        onSkillClick={handleTreeSkillClick}
        onCloseNotesOverlay={handleCloseNotesOverlay}
        findSubcourseByLevel={findSubcourseByLevel}
        updateUrlParams={updateUrlParams}
        userClickedSkillRef={userClickedSkillRef}
        treeClickedSkillIdRef={treeClickedSkillIdRef}
        setSelectedSkillId={setSelectedSkillId}
      />
    );
  }

  // Desktop view
  return (
    <LevelPanelDesktopView
      level={level}
      levelNumber={levelNumber}
      progress={progress}
      courseTreeLoading={courseTreeLoading}
      isMastered={isMastered}
      practices={practices}
      vocabReady={vocabReady}
      vocabStats={vocabStats}
      notes={notes}
      notesLoading={notesLoading}
      displayLessons={displayLessons}
      skills={skills}
      skillsCache={skillsCache}
      desktopExpandedLessonIds={desktopExpandedLessonIds}
      treeLoadingLessonId={treeLoadingLessonId}
      selectedSkillId={selectedSkillId}
      activeLessonId={activeLessonId}
      isLessonsLoading={isLessonsLoading}
      shouldCollapseLessonList={shouldCollapseLessonList}
      onQuickPracticeStart={handleQuickPracticeStart}
      onWritingPracticeStart={handleWritingPracticeStart}
      onSpeakingPracticeStart={handleSpeakingPracticeStart}
      onDrillVocabStart={handleDrillVocabStart}
      onLessonClick={handleLessonClick}
      onSkillSelect={handleSkillSelect}
      onToggleLesson={handleDesktopTreeToggleLesson}
      onReadingMaterialClick={handleReadingMaterialClick}
    />
  );
};

export default LevelPanel;
